import { PopoverOrigin } from '@mui/material';

import {
  button,
  buttonPrimary,
  colors,
  fonts,
  menuPaper,
  navItem,
  radius,
  sizes,
  thinScrollbar,
} from '../../theme';

const logo = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  cursor: 'pointer',
};

// Only this area scrolls: the side menu stays in place.
const main = {
  flexGrow: 1,
  minWidth: 0,
  minHeight: 0,
  overflowY: 'auto',
  // The scrollbar space is always kept: the filters above a list don't shift when the list shows up.
  scrollbarGutter: 'stable',
  ...thinScrollbar,
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  boxSizing: 'border-box',
};

const authButton = {
  width: '100%',
  height: 40,
  fontSize: 14,
};

const styles = {
  // The side menu is a raised card, the same gutter around it and the content.
  root: {
    height: '100vh',
    p: `${sizes.gutter}px`,
    display: 'flex',
    gap: `${sizes.gutter}px`,
    overflow: 'hidden',
    boxSizing: 'border-box',
    backgroundColor: colors.background,
    color: colors.text,
    fontFamily: fonts.text,
  },
  sideNav: {
    width: sizes.sideNav,
    flexShrink: 0,
    height: '100%',
    px: 1.5,
    pt: 1.75,
    pb: 1.75,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: '18px',
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.6)',
    boxSizing: 'border-box',
  },
  // Starts where the icons of the items start.
  navLogo: {
    ...logo,
    px: 1.25,
    pt: 0.75,
    pb: 2.25,
  },
  logoImg: {
    height: 28,
  },
  logoText: {
    fontFamily: fonts.display,
    fontWeight: 700,
    fontSize: 26,
    // The box of the font is taller than the capitals: centered on the logo, not on the box.
    lineHeight: 1,
    pt: '2px',
    letterSpacing: '0.5px',
    color: colors.text,
  },
  navList: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
  },
  navLink: {
    textDecoration: 'none',
  },
  // Same size as the icons' box: the avatar sits where the other icons sit.
  navAvatar: {
    width: 22,
    height: 22,
    backgroundColor: colors.surfaceRaised,
  },
  // Who is logged in (admin): not a page, nothing to click.
  navMe: {
    ...navItem,
    cursor: 'default',
    '&:hover': {},
  },
  navBottom: {
    mt: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  },
  guestActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  guestSignup: {
    ...buttonPrimary,
    ...authButton,
  },
  guestLogin: {
    ...button,
    ...authButton,
    backgroundColor: 'transparent',
    borderColor: '#3A3A3A',
  },
  main,
  // Phone (the admin dashboard alone): the top bar of before, the logo and the account menu in it.
  rootMobile: {
    height: '100vh',
    pt: `${sizes.topBar}px`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxSizing: 'border-box',
    backgroundColor: colors.background,
    color: colors.text,
    fontFamily: fonts.text,
  },
  topBarMobile: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1300,
    height: sizes.topBar,
    px: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.surface,
    borderBottom: `1px solid ${colors.border}`,
    boxSizing: 'border-box',
  },
  logo,
  logoImgMobile: {
    height: 30,
  },
  meButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    height: sizes.button,
    px: 1,
    borderRadius: radius.button,
    color: colors.text,
    textTransform: 'none',
    fontFamily: fonts.text,
    fontSize: 14,
    fontWeight: 500,
    '&:hover': {
      backgroundColor: colors.hoverItem,
    },
  },
  chevron: {
    transition: 'transform 0.2s',
  },
  chevronOpen: {
    transform: 'rotate(180deg)',
  },
  // A grey circle while the image loads: the avatar doesn't pop in after the name.
  meAvatar: {
    width: 32,
    height: 32,
    backgroundColor: colors.surfaceRaised,
  },
  mainMobile: {
    ...main,
    p: 2,
  },
  menuAnchorOrigin: {
    vertical: 'bottom',
    horizontal: 'right',
  } as PopoverOrigin,
  menuTransformOrigin: {
    vertical: 'top',
    horizontal: 'right',
  } as PopoverOrigin,
  menuPaperProps: {
    sx: {
      ...menuPaper,
      mt: 1,
    },
  },
};

export default styles;
